import { defineStore } from 'pinia'
import { convertArrayToObject } from 'src/utils'
import { useAuthStore } from 'src/stores/auth'
import { usePersistedStore } from 'src/stores/persisted'

export const useWishlistStore = defineStore('wishlist', {
  state: () => ({
    wishlist: {},
    allowAddToCart: false,
    selectedFulfillmentMethodID: '',
    wishlistCount: 0,
    loading: true,
    totalCount: 0,
    markedAsWishlist: {},
    sortByOptions: [
      { value: 'Price Low to High', label: 'Price Low to High' },
      { value: 'Price High to Low', label: 'Price High to Low' },
      { value: 'Best Sellers', label: 'Best Sellers' },
      { value: 'Customer Reviews', label: 'Customer Reviews (Ratings)' },
    ],
    defaultParams: { sortBy: 'Price Low to High', PageSize: 20, Page: 1 },
    allWishlist: [],
  }),
  getters: {
    allWishlistName(state) {
      return state.allWishlist
    },
    isProductMarkedAsWishlist(state) {
      return (productId, isWishlist) => {
        if (state.markedAsWishlist[productId]) {
          return state.markedAsWishlist[productId].isWish
        }

        return isWishlist
      }
    },
    wishlistItemsCount(state) {
      let count = 0
      for (let key in state.wishlist) {
        let item = state.wishlist[key]
        if (this.isProductMarkedAsWishlist(item.productID, item.isWished)) {
          count++
        }
      }
      return count
    },
    getWishlistNameByID(state) {
      return (wishlistID) => {
        for (let wishlistItem of state.allWishlist) {
          if (wishlistItem.wishListID == wishlistID)
            return wishlistItem.wishListName
        }
      }
    },
  },
  actions: {
    async toggleProductIsWishlist(products = []) {
      try {
        let { data } = await this.$axiosInstance.post('/Wishlist', products)

        if (data?.data) {
          this.wishlistCount = data.data.wishProductCount
          const wishlist = convertArrayToObject(
            data.data.wishProducts,
            'productID'
          )
          this.markedAsWishlist = Object.assign({}, this.markedAsWishlist, {
            ...wishlist,
          })
        }
        return data
      } catch (error) {
        return error
      }
    },
    /**
     * @param {{ WishListID: string, params: object, append?: boolean }} opts
     * @param {boolean} [opts.append] If true and `params.Page` > 1, merge into existing wishlist (infinite scroll).
     *        If false/omitted, each page replaces `wishlist` (pagination mode).
     */
    async getWishlistProducts({ WishListID, params, append = false }) {
      const authStore = useAuthStore(),
        persistedStore = usePersistedStore()
      if (!persistedStore.currentLocationId || !authStore.customerExists)
        return false

      try {
        if (params.Page == 1) this.loading = true
        let { data } = await this.$axiosInstance.get(
          `/Wishlist/Products/${WishListID}`,
          { params }
        )

        this.totalCount = data.data.count
        let wishlistProducts = convertArrayToObject(
          data.data.items,
          'productID'
        )

        if (params.Page == 1) {
          this.wishlist = Object.assign({}, wishlistProducts)
          this.loading = false
        } else if (append) {
          this.wishlist = Object.assign({}, this.wishlist, wishlistProducts)
        } else {
          this.wishlist = Object.assign({}, wishlistProducts)
        }

        return data.data
      } catch (error) {}
    },
    async removeAllItemFromWishlist(body) {
      let { data } = await this.$axiosInstance.post(
        '/Wishlist/DeleteProducts',
        body
      )

      if (data.success) {
        this.totalCount = this.totalCount - body.productIds.length
        body.productIds.forEach((productId) => {
          delete this.wishlist[productId]
        })
      }
      return data
    },
    enableAddToCartForWishlist(fulFillmentMethodID) {
      this.allowAddToCart = true
      this.selectedFulfillmentMethodID = fulFillmentMethodID
    },
    async addToWishlist(body) {
      try {
        let { data } = await this.$axiosInstance.post('/Wishlist', body)
        return data
      } catch (error) {
        return error
      }
    },
    async createNewWishlist(wishListName) {
      try {
        let { data } = await this.$axiosInstance.post('/Wishlist/Add', {
          wishListName,
        })

        if (data?.success) {
          this.getAllWishlist()
          return data
        }
      } catch (error) {
        return error
      }
    },
    async getAllWishlist() {
      const persistedStore = usePersistedStore()
      try {
        this.loading = true
        let params = {
          locationID: persistedStore.currentLocationId,
          zipCode: persistedStore.locationOrShippingZip,
        }

        let { data } = await this.$axiosInstance.get('v1/Wishlist', {
          params,
        })
        if (data?.data) this.allWishlist = Object.assign([], data.data)
      } catch (error) {
      } finally {
        this.loading = false
      }
    },
    async deleteWishlist(wishlistId) {
      try {
        let { data } = await this.$axiosInstance.delete(
          `/Wishlist?wishListID=${wishlistId}`
        )

        if (data?.data) {
          const index = this.allWishlist.findIndex(
            (wishlist) => wishlist.wishListID === wishlistId
          )
          if (index !== -1) {
            this.allWishlist.splice(index, 1)
          }
          return data
        }
      } catch (error) {}
    },
    async editWishlist(params) {
      try {
        let { data } = await this.$axiosInstance.put('/Wishlist', {
          wishListID: params.wishListID,
          wishListName: params.wishListName,
        })

        if (data?.success) {
          this.getAllWishlist()
          return data
        }
      } catch (error) {}
    },
    async deleteWishlistProducts(body) {
      try {
        let { data } = await this.$axiosInstance.post(
          '/Wishlist/DeleteProducts',
          body
        )

        if (data.success) {
          this.totalCount = this.totalCount - 1
          delete this.wishlist[body.productIds[0]]
        }

        return data
      } catch (error) {}
    },
    setWishlist(wishlist) {
      this.wishlist = Object.assign({}, wishlist)
    },
    setTotal(totalCount) {
      this.totalCount = totalCount
    },
  },
})
